/**
 * Error handling components
 * - ErrorBoundary: React error boundary for catching component errors
 * - ErrorMessage: User-friendly error display
 * - ErrorToast: Toast notification for errors
 */

import { Component, type ReactNode } from 'react';

// Error boundary props
interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Fallback UI to show when an error occurs
   */
  fallback?: ReactNode;
  /**
   * Called when an error is caught
   */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * React Error Boundary
 *
 * Catches JavaScript errors in child component tree and displays
 * a fallback UI instead of crashing the whole app.
 *
 * @example
 * <ErrorBoundary fallback={<ErrorMessage />}>
 *   <MyComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Call onError callback if provided
    this.props.onError?.(error, errorInfo);

    // In production, you would send this to an error tracking service like Sentry
    // if (import.meta.env.PROD) {
    //   sendToErrorTracking(error, errorInfo);
    // }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorMessage
          title="Something went wrong"
          message="An unexpected error occurred. Please try again."
          onRetry={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

// User-friendly error display
interface ErrorMessageProps {
  title?: string;
  message: string;
  /**
   * Show technical error details (dev only)
   */
  technicalDetails?: string;
  /**
   * Show retry button
   */
  onRetry?: () => void;
  /**
   * Show go back button
   */
  onGoBack?: () => void;
  /**
   * Custom action button
   */
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

export function ErrorMessage({
  title = 'Error',
  message,
  technicalDetails,
  onRetry,
  onGoBack,
  action,
  className = '',
}: ErrorMessageProps) {
  // Check if we're in development mode
  const isDev = import.meta.env.DEV;

  return (
    <div
      className={`p-6 bg-red-900/20 border border-red-800 rounded-xl ${className}`}
      role="alert"
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
          <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-red-200 mb-1">{title}</h3>
          <p className="text-red-300">{message}</p>

          {isDev && technicalDetails && (
            <details className="mt-4">
              <summary className="text-sm text-red-400 cursor-pointer hover:text-red-300">
                Technical details
              </summary>
              <pre className="mt-2 p-3 bg-gallery-900 rounded text-xs text-gallery-400 overflow-auto max-h-40">
                {technicalDetails}
              </pre>
            </details>
          )}

          <div className="flex flex-wrap gap-3 mt-4">
            {onRetry && (
              <button
                onClick={onRetry}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium transition-colors"
              >
                Try again
              </button>
            )}
            {onGoBack && (
              <button
                onClick={onGoBack}
                className="px-4 py-2 bg-gallery-800 hover:bg-gallery-700 rounded-lg text-sm font-medium transition-colors"
              >
                Go back
              </button>
            )}
            {action && (
              <button
                onClick={action.onClick}
                className="px-4 py-2 bg-gallery-800 hover:bg-gallery-700 rounded-lg text-sm font-medium transition-colors"
              >
                {action.label}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Inline error message (for form fields, etc.)
interface InlineErrorProps {
  message: string;
  className?: string;
}

export function InlineError({ message, className = '' }: InlineErrorProps) {
  return (
    <p className={`text-sm text-red-400 mt-1 ${className}`} role="alert">
      {message}
    </p>
  );
}

// Error state for empty/failed data fetches
interface ErrorStateProps {
  message?: string;
  onRetry?: () => void;
  className?: string;
}

export function ErrorState({
  message = 'Failed to load data',
  onRetry,
  className = '',
}: ErrorStateProps) {
  return (
    <div className={`text-center py-12 ${className}`} role="status">
      <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
        <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <p className="text-gallery-400 mb-4">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="px-6 py-2 bg-gallery-800 hover:bg-gallery-700 rounded-lg text-sm font-medium transition-colors"
        >
          Try again
        </button>
      )}
    </div>
  );
}

// 404 / Not Found error
interface NotFoundProps {
  title?: string;
  message?: string;
  onGoHome?: () => void;
  className?: string;
}

export function NotFound({
  title = 'Page not found',
  message = "The page you're looking for doesn't exist or has been moved.",
  onGoHome,
  className = '',
}: NotFoundProps) {
  return (
    <div className={`text-center py-20 ${className}`}>
      <div className="text-8xl font-bold text-gallery-800 mb-4">404</div>
      <h1 className="text-2xl font-semibold mb-2">{title}</h1>
      <p className="text-gallery-400 mb-8 max-w-md mx-auto">{message}</p>
      {onGoHome && (
        <button
          onClick={onGoHome}
          className="px-6 py-3 bg-accent-600 hover:bg-accent-700 rounded-lg font-medium transition-colors"
        >
          Go to homepage
        </button>
      )}
    </div>
  );
}

// Network error state
interface NetworkErrorProps {
  onRetry?: () => void;
  className?: string;
}

export function NetworkError({ onRetry, className = '' }: NetworkErrorProps) {
  return (
    <div className={`text-center py-12 ${className}`} role="status">
      <div className="w-16 h-16 mx-auto mb-4 bg-yellow-500/20 rounded-full flex items-center justify-center">
        <svg className="w-8 h-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
          />
        </svg>
      </div>
      <h3 className="text-lg font-semibold mb-2">Connection error</h3>
      <p className="text-gallery-400 mb-4">Please check your internet connection and try again.</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="px-6 py-2 bg-gallery-800 hover:bg-gallery-700 rounded-lg text-sm font-medium transition-colors"
        >
          Retry
        </button>
      )}
    </div>
  );
}
